import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ZODIAC_SIGNS, getSunSign } from '@yadviga/shared'
import { api } from '../../api/client'
import { useSession } from '../../store/user.store'
import { Button } from '../../components/ui/Button'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { ErrorState } from '../../components/ui/ErrorState'
import { MarkdownText } from '../../components/ui/Markdown'
import { PageHeader } from '../../components/ui/PageHeader'
import { BirthModal } from './birth-modal'

export function AstroHoroscopeRoute() {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)
  const qc = useQueryClient()
  const [birthOpen, setBirthOpen] = useState(false)

  const horoscopeQuery = useQuery({
    queryKey: ['horoscope', 'today'],
    queryFn: () => api.horoscopeToday(),
    enabled: Boolean(user?.birthDate),
    staleTime: 1000 * 60 * 60,
  })

  if (!user?.birthDate) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <PageHeader title={t('forecast.horoscope_title')} backTo="/astro" />
        <p className="text-sage-700">{t('natal.no_birth')}</p>
        <Button size="lg" onClick={() => setBirthOpen(true)}>{t('natal.set_birth')}</Button>
        <BirthModal open={birthOpen} onClose={() => setBirthOpen(false)} onSaved={() => qc.invalidateQueries()} />
      </div>
    )
  }

  const [_y, m, d] = user.birthDate.split('-').map(Number)
  const sunSign = getSunSign(m!, d!)
  const lang = (user.language ?? 'ru') as 'ru' | 'en'
  const signLabel =
    ZODIAC_SIGNS.find((s) => s.id === sunSign)?.[lang === 'en' ? 'nameEn' : 'nameRu'] ?? sunSign

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={t('forecast.horoscope_title')}
        backTo="/astro"
        right={
          <span className="rounded-full bg-sage-100/70 px-3 py-1 text-xs text-sage-700">
            {signLabel}
          </span>
        }
      />

      <section>
        {horoscopeQuery.isLoading && <LoadingDots compact />}
        {horoscopeQuery.isError && <ErrorState onRetry={() => horoscopeQuery.refetch()} />}
        {horoscopeQuery.data && (
          <div className="rounded-3xl bg-white/70 p-5 ring-1 ring-sage-200/60">
            <MarkdownText>{horoscopeQuery.data.text}</MarkdownText>
          </div>
        )}
      </section>
    </div>
  )
}
